<script setup>
import { ref, computed } from 'vue';

const props = defineProps({
  conversation: {
    type: Object,
    required: true,
  },
  selectMode: {
    type: Boolean,
    default: false,
  },
  selected: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['click', 'toggle-select']);

const contact = computed(() => props.conversation.meta?.sender || {});
const inboxName = computed(() => props.conversation.inbox?.name || '');

const lastMessage = computed(() => {
  const messages = props.conversation.messages || [];
  if (!messages.length) return null;
  return messages[messages.length - 1];
});

const lastMessagePreview = computed(() => {
  if (!lastMessage.value) return 'Sem mensagens';
  const content = lastMessage.value.content || '';
  if (content.length > 70) return content.substring(0, 70) + '…';
  return (
    content ||
    (lastMessage.value.attachments?.length ? '📎 Anexo' : 'Sem mensagens')
  );
});

const isIncoming = computed(() => {
  return lastMessage.value?.message_type === 0;
});

// "Waiting since" — time since the customer sent a message awaiting agent reply
const waitingSince = computed(() => {
  const ts = props.conversation.waiting_since;
  if (!ts) return null;
  const now = Date.now();
  const since = ts * 1000;
  const diffMs = now - since;
  if (diffMs < 60000) return null; // under 1 min, ignore
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 60) return `${diffMin}min`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}hr`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d`;
});

// Customer is waiting if waiting_since is set (API is authoritative)
const isWaitingForReply = computed(() => !!waitingSince.value);

const waitTime = computed(() => {
  const lastActivity = props.conversation.last_activity_at;
  if (!lastActivity) return '';
  const now = Date.now();
  const activityTime = new Date(lastActivity * 1000).getTime();
  const diffMs = now - activityTime;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays}d`;
});

const priorityConfig = computed(() => {
  const p = props.conversation.priority;
  if (!p || p === 'none') return null;
  const configs = {
    urgent: { badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400', icon: 'i-lucide-alert-triangle', label: 'Urgente' },
    high: { badge: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400', icon: 'i-lucide-arrow-up', label: 'Alta' },
    medium: { badge: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400', icon: 'i-lucide-minus', label: 'Média' },
    low: { badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400', icon: 'i-lucide-arrow-down', label: 'Baixa' },
  };
  return configs[p] || null;
});

const statusBadge = computed(() => {
  const status = props.conversation.status;
  if (status === 'pending')
    return {
      text: 'Pendente',
      class:
        'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/20 dark:text-yellow-400',
    };
  if (status === 'snoozed')
    return {
      text: 'Adiado',
      class: 'bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400',
    };
  return null;
});

// CRM Deal info
const dealInfo = computed(() => {
  const deal = props.conversation.crm_deal;
  if (!deal) return null;
  return {
    pipelineName: deal.pipeline_name,
    stageName: deal.stage_name,
  };
});

const contactInitial = computed(() => {
  const name = contact.value.name || '?';
  return name[0].toUpperCase();
});

const imgError = ref(false);
const onImgError = () => {
  imgError.value = true;
};
</script>

<template>
  <div
    class="group cursor-pointer rounded-lg bg-white dark:bg-n-slate-2 border transition-all duration-150 shadow-[0_1px_2px_0_rgba(0,0,0,0.04)] hover:shadow-[0_2px_8px_0_rgba(0,0,0,0.08)]"
    :class="selected ? 'border-blue-400 bg-blue-50/30 dark:bg-blue-900/10 ring-1 ring-blue-300/50' : 'border-n-weak/80 hover:border-n-slate-6'"
    @click="selectMode ? emit('toggle-select', conversation) : emit('click', conversation)">
    <div class="px-3 py-2">
      <div class="flex items-center justify-between gap-2 mb-1">
        <div class="flex items-center gap-2 min-w-0">
          <input
            v-if="selectMode"
            type="checkbox"
            :checked="selected"
            class="size-4 flex-shrink-0 accent-blue-500 cursor-pointer"
            @click.stop
          />
          <div class="flex-shrink-0">
            <img v-if="contact.thumbnail && !imgError" :src="contact.thumbnail" :alt="contact.name"
              class="size-6 rounded-full object-cover" @error="onImgError" />
            <div v-else
              class="size-6 rounded-full bg-n-slate-3 dark:bg-n-slate-8 text-n-slate-11 flex items-center justify-center text-[10px] font-semibold">
              {{ contactInitial }}
            </div>
          </div>
          <span class="text-[13px] font-medium text-n-slate-12 truncate leading-tight">
            {{ contact.name || 'Contato' }}
          </span>
        </div>
        <span class="text-[9px] font-medium flex-shrink-0 px-1.5 py-0.5 rounded bg-n-alpha-2 text-n-slate-9">
          {{ waitTime }}
        </span>
      </div>

      <p class="text-[11px] text-n-slate-10 leading-snug line-clamp-1 pl-8 mb-1.5">
        <span v-if="isIncoming" class="i-lucide-corner-down-right size-2.5 inline-block mr-0.5 -mt-px text-n-slate-8" />
        <span v-else class="i-lucide-corner-up-right size-2.5 inline-block mr-0.5 -mt-px text-n-slate-8" />
        {{ lastMessagePreview }}
      </p>

      <div class="flex items-center gap-1 pl-8 overflow-x-auto no-scrollbar">
        <span
          class="inline-flex items-center text-[10px] font-semibold px-1.5 py-px rounded bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400 whitespace-nowrap">
          #{{ conversation.id }}
        </span>
        <span v-if="priorityConfig"
          class="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-px rounded whitespace-nowrap"
          :class="priorityConfig.badge">
          <span :class="priorityConfig.icon" class="size-2.5" />
          {{ priorityConfig.label }}
        </span>
        <span v-if="statusBadge"
          class="inline-flex items-center text-[10px] font-semibold px-1.5 py-px rounded whitespace-nowrap"
          :class="statusBadge.class">
          {{ statusBadge.text }}
        </span>
        <span v-if="dealInfo"
          class="inline-flex items-center gap-0.5 text-[10px] font-semibold px-1.5 py-px rounded bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 whitespace-nowrap">
          <span class="i-lucide-git-branch size-2.5 flex-shrink-0" />
          {{ dealInfo.stageName || dealInfo.pipelineName }}
        </span>
      </div>
    </div>
  </div>
</template>
